import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image, Link, Font } from '@react-pdf/renderer';
import ReactPDFChart from 'react-pdf-charts';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  ErrorBar,
  Label,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis
} from 'recharts';
// Register font
import LogoWithSloganGreen from '../../../assets/images/LogoWithSloganGreen.png';
import LogoWithSlogan from '../../../assets/images/LogoWithSlogan.png';
import PDFIconHome from '../Assets/PDFIconHome.png';
import PDFIconMenu from '../Assets/PDFIconMenu.png';
import PDFIconStems from '../Assets/PDFIconStems.png';
import PDFIconPinOutline from '../Assets/PDFIconPinOutline.png';
import PDFIconPlantWhite from '../Assets/PDFIconPlantWhite.png';
import CloudRain from '../Assets/CloudRain.png';
import Wind from '../Assets/Weather/Wind.png';
import Humidity from '../Assets/Weather/Humidity.png';
import Uv from '../Assets/Weather/Uv.png';
import { Farm, FarmSection, Field, FieldForecast, PDFFarmTotal, Variety } from 'Context/types';
import {
  calculateCentroid,
  getRandomHexColor,
  graphColors,
  kFormatter,
  sortVarietyProductionCategories
} from 'Helpers/helpers';
import WeeklyWeatherOverview from './Weather/WeeklyWeatherOverview';
import moment from 'moment';
import SummaryPage from './WeeklyReport/SummaryPage';
import PageHeader from './WeeklyReport/PageHeader';
import { Tooltip } from 'rsuite';

//--PDF INITIALIZATIONS
Font.register({
  family: 'Be Vietnam Pro',
  fonts: [
    // { src: '../Assets/BeVietnamPro-Regular.ttf', fontWeight: 'normal' },
    { src: 'fonts/BeVietnamPro-Regular.ttf', fontWeight: 'normal' },
    { src: '/fonts/BeVietnamProBold.ttf', fontWeight: 'bold' }
  ]
  // src: 'https://fonts.googleapis.com/css2?family=Be+Vietnam+Pro:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap'
});
// Create styles
const styles = StyleSheet.create({
  page: {
    flexDirection: 'row',
    backgroundColor: '#ffffff',
    fontFamily: 'Be Vietnam Pro'
  },
  section: {
    margin: 30,
    padding: 10,
    flexGrow: 1
  },
  section_title: {
    fontSize: 12,
    marginBottom: 8,
    fontFamily: 'Be Vietnam Pro'
  },
  green_page_title: {
    fontSize: 11,
    marginRight: 200
  },
  text: { margin: 12, fontSize: 14, textAlign: 'justify', fontFamily: 'Times-Roman' },
  info_card: {
    width: '180px',
    height: '80px',
    marginLeft: 12,
    paddingTop: 12,
    borderRadius: 6,
    backgroundColor: '#00A896',
    color: '#000000',
    alignItems: 'center'
  },
  info_card_bottom: {
    backgroundColor: '#CCEEEA',
    width: '100%',
    height: '50%',
    alignItems: 'center',
    borderRadius: 6
  },
  info_card_top: { width: '100%', height: '50%', alignItems: 'center', borderRadius: 6 },
  info_card_stems_icon: { width: '20px', height: 20, marginTop: 12 },
  info_card_stems_text: { fontSize: 12, color: '#000' },
  cover_page_link: {
    fontFamily: 'Be Vietnam Pro',
    color: '#141414',
    fontSize: 16,
    textDecoration: 'underline',
    marginTop: 6,
    marginBottom: 6
  },
  field_grid_item: {
    width: '46px',
    height: '30px',
    marginRight: 8,
    marginBottom: 8,
    paddingTop: 8,
    borderRadius: 6,
    backgroundColor: '#53A5FF',
    alignItems: 'center',
    color: '#ffffff',
    fontSize: 9,
    textDecoration: 'underline'
  },
  field_details: { fontSize: 12, marginBottom: 6 },
  bold_text: {
    fontWeight: 'bold'
  },
  field_weekly_forecast_container: { flexDirection: 'row', height: 34, marginBottom: 4 },
  field_weekly_forecast_left: {
    width: 50,
    backgroundColor: '#CCEEEA',
    color: '#000',
    borderTopLeftRadius: 12,
    borderBottomLeftRadius: 12,
    paddingTop: 7,
    paddingLeft: 10,
    paddingRight: 12
  },
  field_weekly_forecast_center: {
    backgroundColor: '#00A896',
    color: '#fff',
    padding: 8,
    flexDirection: 'row',
    alignItems: 'center',
    width: 95
  },
  field_weekly_forecast_right: {
    backgroundColor: '#00332d',
    color: '#fff',
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
    padding: 8,
    flexDirection: 'row',
    alignItems: 'center',
    width: 95
  },
  daily_weather_item: { display: 'flex', flexDirection: 'row', marginLeft: 12, marginBottom: 4 },
  daily_weather_item_icon: { width: 14, marginRight: 4, padding: 2 }
});
// --- END PDF INITIALIZATIONS

const renderLabelList = (props: any) => {
  const { x, y, width, height, value, index, noKFormat } = props;

  // Custom label styles
  const customStyle = {
    fontSize: width < 50 ? 9 : 10,
    fontWeight: 'bold',
    // textAnchor: 'middle',
    fill: '#black', // white text color
    backgroundColor: '#fff', // black background
    padding: '4px 8px',
    borderRadius: '4px',
    transform: `rotate(-25 ${x} ${y})` // move label slightly above the bar
  };

  return (
    <text x={x + (width < 50 ? 0 : +(width / 2.3))} y={y + height / 1.8} style={customStyle}>
      {value > 0 ? (!noKFormat ? kFormatter(value, 0) : value) : ''}
    </text>
  );
};

// ---END GRAPH

// MAP FUNCTIONS
const map_zoom_level = { field: 18, farm: 15 };
//---END MAP FUNCTIONS

// ---START WEATHER FUNCTIONS
// Create Document Component
export type WeeklyPDFReportProps = {
  farm: Farm;
  farmSection: FarmSection | null;
  varieties: Variety[];
  week_no: number;
  fields: Field[];
  total_farm_forecast: number;
  graph_data: any;
  variety_forecasts: any[];
  date_string: string;
  field_forecasts: Record<string, FieldForecast>;
  weather_days: moment.Moment[];
  weather_data: any;
  weather_graph_data: any;
  weather_week_overview: any[];
  variety_grid_config: any;
  farm_section_totals: PDFFarmTotal;
};

const WeeklyPDFReport = (props: WeeklyPDFReportProps) => (
  <Document
    key={1}
    title={`${props.farm.customer.name} -${props.farm.name} Week ${props.week_no} Forecast`}
  >
    {/* TITLE PAGE */}
    <Page size="A4" style={styles.page} orientation="landscape">
      <View style={styles.section} id="cover-page">
        <Image style={{ width: '150px' }} src={LogoWithSloganGreen}></Image>
        <Text style={{ marginTop: 154, fontSize: 14 }}>
          <Text style={{ fontWeight: 'bold', fontFamily: 'Be Vietnam Pro' }}>
            {props.farm.customer.name.toLowerCase() === props.farm.name.toLowerCase()
              ? `${props.farm.name}`
              : `${props.farm.customer.name}, ${props.farm.name}`}{' '}
            {props.farmSection ? ` (${props.farmSection.name}) ` : ''}
            {' - '}
          </Text>
          <Text>Week {props.week_no}</Text>
        </Text>

        <Text style={{ marginTop: 8, marginBottom: 16, fontSize: 42, color: '#00A896' }}>
          FARM REPORT
        </Text>
        <Link src="#overall-farm-report" style={styles.cover_page_link}>
          Overall Farm Report
        </Link>
        {props.farmSection && (
          <Link src="#farm-section-summry" style={styles.cover_page_link}>
            {props.farmSection?.name} Summary
          </Link>
        )}
        <Link src="#field-nav-list" style={styles.cover_page_link}>
          Field Specific Data
        </Link>
        <Link src="#weekly-weather-report" style={styles.cover_page_link}>
          Weekly Weather Report
        </Link>
      </View>

      <View
        style={{ backgroundColor: '#CCEEEA', width: 22, height: '100%', marginRight: 180 }}
      ></View>
    </Page>
    {/* OVERALL FARM REPORT PAGE PAGE */}
    <Page
      size="A4"
      orientation="landscape"
      style={{
        ...styles.page,
        backgroundColor: '#e8f3ffff'
      }}
    >
      <View style={styles.section} id="milestone-overview">
        {/* HEADER SECTION */}
        <PageHeader
          left_header={props.farm.name}
          left_subheader={`${props.farm.city.replaceAll('_', ' ')}, ${props.farm.country}`}
          center_header={`Week ${props.week_no}`}
          center_subheader={props.date_string}
          week_no={props.week_no}
          styles={styles}
        ></PageHeader>

        {/* summary cards section */}
        <View style={{ flexDirection: 'row', marginTop: 24 }}>
          {/* latest forecast */}
          <View
            style={{
              ...styles.info_card,
              backgroundColor: '#98C9FF'
            }}
          >
            <Text
              style={{ fontSize: 16, fontWeight: 'medium', marginBottom: 4, textAlign: 'left' }}
            >
              {props.total_farm_forecast.toLocaleString()}
            </Text>
            <Text style={{ fontSize: 12, textAlign: 'left' }}>Lima Forecast</Text>
          </View>

          {/* total varieties */}
          <View
            style={{
              ...styles.info_card,
              backgroundColor: '#98C9FF'
            }}
          >
            <Text style={{ fontSize: 16, fontWeight: 'medium', marginBottom: 4 }}>
              {props.variety_forecasts.length}
            </Text>
            <Text style={{ fontSize: 12 }}>
              Variet{props.variety_forecasts.length == 1 ? 'y' : 'ies'}
            </Text>
          </View>

          {/* total fields */}
          <View
            style={{
              ...styles.info_card,
              backgroundColor: '#98C9FF'
            }}
          >
            <Text style={{ fontSize: 16, fontWeight: 'medium', marginBottom: 4 }}>
              {props.fields.length}
            </Text>
            <Text style={{ fontSize: 12 }}>Active Field{props.fields.length == 1 ? '' : 's'}</Text>
          </View>
          {/* 2 weeks out forecast */}
          <View
            style={{
              ...styles.info_card,
              backgroundColor: '#98C9FF'
            }}
          >
            <Text style={{ fontSize: 16, fontWeight: 'medium', marginBottom: 4 }}>
              {props.farm.number_of_active_fields}
            </Text>
            <Text style={{ fontSize: 12 }}>Total Fields</Text>
          </View>
        </View>

        {/* Overall Farm graph section */}
        <View
          style={{
            marginTop: 40,
            marginLeft: 10,
            left: 0,
            paddingTop: 30,
            paddingBottom: 20,
            paddingRight: 20,
            paddingLeft: 20,
            backgroundColor: '#ffffff',
            borderRadius: 6
          }}
        >
          <ReactPDFChart style={{ left: -30 }}>
            <ComposedChart
              width={750}
              height={250}
              data={props.graph_data?.farm || []}
              style={{ left: 0 }}
              barGap={4}
              margin={{ top: 30 }}
              // barSize={18}
            >
              <CartesianGrid strokeDasharray="0" vertical={false} />
              <XAxis dataKey="name" interval={0} tick={{ fontSize: 11, fontWeight: 'bold' }} />
              <YAxis
                tickCount={6}
                tickFormatter={(value: string) => value && kFormatter(Number(value), 1)}
                tick={{ fontSize: 11, fontWeight: 'bold' }}
              />
              <Legend iconType="circle" iconSize={10} width={830} align="left" />
              <Bar
                dataKey={'Actuals'}
                fill={'#626262'}
                isAnimationActive={false}
                radius={[3, 3, 0, 0]}
              >
                <LabelList
                  dataKey="Actuals"
                  position="top"
                  formatter={(value: number) => kFormatter(value, 1) ?? ''}
                  color="#000"
                />
              </Bar>
              <Bar
                dataKey={'Forecasts'}
                fill={'#53A5FF'}
                isAnimationActive={false}
                radius={[3, 3, 0, 0]}
              >
                <LabelList
                  dataKey="Forecasts"
                  position="top"
                  formatter={(value: number) => kFormatter(value, 1) ?? ''}
                  color="#000"
                />
              </Bar>
            </ComposedChart>
          </ReactPDFChart>
        </View>
      </View>
    </Page>
    {/* FARM SECTION SUMMARY */}
    {props.farmSection && (
      <Page
        size="A4"
        orientation="landscape"
        style={{
          ...styles.page,
          backgroundColor: '#e8f3ffff'
        }}
      >
        <SummaryPage
          left_header={props.farmSection.name}
          left_subheader={`${props.farm.city.replaceAll('_', ' ')}, ${props.farm.country}`}
          center_header={`Week ${props.week_no}`}
          center_subheader={props.date_string}
          week_no={props.week_no}
          styles={styles}
          total_forecast={props.farm_section_totals.forecast}
          varieties_count={props.varieties.length}
          active_fields_count={props.farm_section_totals.active_fields_count}
          total_fields_count={props.farm_section_totals.total_fields_count}
          graph_data={props.graph_data.section || []}
        ></SummaryPage>
      </Page>
    )}
    {/*  MULTI-VARIETY GRAPH  */}
    {props.variety_forecasts.length > 1 && (
      <>
        {Array.from({ length: Math.ceil(props.variety_forecasts.length / 15) }).map(
          (_, pageIndex) => {
            const start = pageIndex * 15;
            const end = start + 15;
            // Sort varieties by color name, then shade, then variety name
            const sortedVarieties = [...props.graph_data.varieties].sort((a: any, b: any) => {
              const colorA = a.colors?.[0]?.name?.toLowerCase() || '';
              const colorB = b.colors?.[0]?.name?.toLowerCase() || '';
              if (colorA !== colorB) return colorA.localeCompare(colorB);
              const shadeA = a.colors?.[0]?.shade?.toLowerCase() || '';
              const shadeB = b.colors?.[0]?.shade?.toLowerCase() || '';
              if (shadeA !== shadeB) return shadeA.localeCompare(shadeB);
              const nameA = a.variety_name?.toLowerCase() || '';
              const nameB = b.variety_name?.toLowerCase() || '';
              return nameA.localeCompare(nameB);
            });
            const varietyPageData = sortedVarieties.slice(start, end);
            return (
              <Page key={pageIndex} size="A4" orientation="landscape" style={styles.page}>
                <View style={styles.section} id={`variety-summary-page-${pageIndex + 1}`}>
                  {/* HEADER SECTION */}
                  <PageHeader
                    left_header={`Variety Summary (Page ${pageIndex + 1})`}
                    left_subheader={`${props.farm.name}`}
                    center_header={`Week ${props.week_no}`}
                    center_subheader={props.date_string}
                    week_no={props.week_no}
                    styles={styles}
                  ></PageHeader>
                  <View
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      width: '805px',
                      height: '500px'
                    }}
                  >
                    <View
                      style={{
                        display: 'flex',
                        marginRight: '1%',
                        flexDirection: 'column',
                        justifyContent: 'flex-start', // Changed from 'space-evenly' to 'flex-start'
                        flexWrap: 'wrap',
                        width: `${0.05 * 805}px`,
                        height: '476px',
                        alignItems: 'flex-start'
                      }}
                    >
                      {Array.from({
                        length:
                          pageIndex === props.variety_grid_config.number_of_pages - 1
                            ? Math.ceil(
                                varietyPageData.length / props.variety_grid_config.items_per_row
                              )
                            : props.variety_grid_config.rows_per_page
                      }).map((_, index) => (
                        <View
                          key={index}
                          style={{
                            marginTop: '1%',
                            marginBottom: '8px',
                            paddingTop: '40px',
                            alignItems: 'flex-end',
                            width: '100%',
                            height:
                              typeof props.variety_grid_config.item_height === 'string' &&
                              props.variety_grid_config.item_height.endsWith('%')
                                ? (parseFloat(props.variety_grid_config.item_height) / 100) * 476
                                : props.variety_grid_config.item_height * 476
                          }}
                        >
                          {varietyPageData[0]?.data.map((d: any, i: number) => (
                            <Text
                              key={i}
                              style={{
                                fontSize: '8px',
                                marginBottom: 20,
                                fontFamily: 'Be Vietnam Pro',
                                fontWeight: 600,
                                color: '#000'
                              }}
                            >
                              {d.name}
                            </Text>
                          ))}
                        </View>
                      ))}
                    </View>
                    <View
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-start',
                        flexWrap: 'wrap',
                        width: `${0.91 * 805}px`,
                        height: '100%'
                      }}
                    >
                      {varietyPageData.map((variety_graph_data: any, index: number) => (
                        <View
                          key={index}
                          style={{
                            marginTop: '1%',
                            marginRight: '1%',
                            alignItems: 'center',
                            width: props.variety_grid_config.item_width,
                            height: props.variety_grid_config.item_height,
                            backgroundColor: '#F5F5F5',
                            paddingLeft: 4,
                            paddingRight: 4
                          }}
                        >
                          <Text
                            style={{
                              fontSize: '11px',
                              marginBottom: 0,
                              marginTop: 6,
                              fontFamily: 'Be Vietnam Pro',
                              fontWeight: 'medium'
                            }}
                          >
                            {variety_graph_data.variety_name}
                          </Text>
                          {variety_graph_data.colors?.[0] && (
                            <Text
                              style={{
                                fontSize: '7px',
                                marginBottom: 2,
                                fontFamily: 'Be Vietnam Pro',
                                fontWeight: 'normal',
                                color: '#666'
                              }}
                            >
                              {variety_graph_data.colors[0].name}
                              {variety_graph_data.colors[0].shade
                                ? ` - ${variety_graph_data.colors[0].shade}`
                                : ''}
                            </Text>
                          )}
                          <ReactPDFChart>
                            <BarChart
                              width={
                                typeof props.variety_grid_config.item_width === 'string' &&
                                props.variety_grid_config.item_width.endsWith('%')
                                  ? (parseFloat(props.variety_grid_config.item_width) / 100) * 750
                                  : props.variety_grid_config.item_width * 750
                              }
                              height={
                                typeof props.variety_grid_config.item_height === 'string' &&
                                props.variety_grid_config.item_height.endsWith('%')
                                  ? ((parseFloat(props.variety_grid_config.item_height) + 10) /
                                      100) *
                                    500
                                  : (props.variety_grid_config.item_height + 10) * 500
                              }
                              data={variety_graph_data.data}
                              layout="vertical"
                              margin={{
                                left: -70,
                                right: 35,
                                top: 10,
                                bottom: -20
                              }}
                            >
                              <YAxis
                                type="category"
                                dataKey="name"
                                fontSize="9px"
                                tick={false}
                                tickCount={5}
                              />
                              <XAxis fontSize="9px" type="number" tick={false} axisLine={false} />
                              <Bar
                                dataKey={'Forecasts'}
                                fill={
                                  variety_graph_data.colors?.[0]?.hex_code || graphColors[index]
                                }
                                isAnimationActive={false}
                                barSize={13}
                                stroke={'#666'}
                                strokeWidth={0.5}
                              >
                                <LabelList
                                  dataKey={'Forecasts'}
                                  position="right"
                                  content={(props: any) => {
                                    const { x, y, width, height, value } = props;
                                    return (
                                      <text
                                        x={x + width + 8}
                                        y={y + height / 2 + 4}
                                        style={{
                                          fill: '#000',
                                          fontWeight: 700,
                                          fontSize: '1.2rem',
                                          textAnchor: 'start'
                                        }}
                                      >
                                        {value > 0 ? kFormatter(value, 1) : ''}
                                      </text>
                                    );
                                  }}
                                />
                              </Bar>
                              <Bar
                                dataKey={'Actuals'}
                                fill={'#000'}
                                isAnimationActive={false}
                                barSize={3}
                              ></Bar>
                            </BarChart>
                          </ReactPDFChart>
                        </View>
                      ))}
                    </View>
                  </View>
                </View>
              </Page>
            );
          }
        )}
      </>
    )}
    {/* FIELD SPECIFIC LIST */}
    {/* TITLE PAGE */}
    <Page size="A4" style={styles.page} orientation="landscape">
      <View style={styles.section} id="field-nav-list">
        <PageHeader
          left_header={`Fields${props.farmSection ? ` (${props.farmSection.name})` : ' '}`}
          left_subheader={`${props.farm.name}, ${props.farm.city.replaceAll('_', ' ')}`}
          center_header={`Week ${props.week_no}`}
          center_subheader={props.date_string}
          week_no={props.week_no}
          styles={styles}
        ></PageHeader>

        <View style={{ flexDirection: 'row', display: 'flex', flexWrap: 'wrap', marginTop: 24 }}>
          {props.fields
            .filter((field: Field) =>
              props.farmSection ? field.farm_section_id === props.farmSection.id : true
            )
            .map((field: Field, index: number) => (
              <Link key={index} style={styles.field_grid_item} src={`#fields/${field.name}`}>
                {field.name}
              </Link>
            ))}
        </View>
      </View>
    </Page>
    {/* FIELD SPECIFIC PAGES */}
    {props.fields
      ?.filter(
        (field: Field) =>
          field.active && (!props.farmSection || field.farm_section_id === props.farmSection.id)
      )
      .map((field: Field, index: number) => (
        <Page key={index} size="A4" orientation="landscape" style={styles.page}>
          <View style={styles.section} id={`fields/${field.name}`}>
            <PageHeader
              left_header={`Field: ${field.name}`}
              left_subheader={` 
                ${props.farm.name}, ${
                props.variety_forecasts.find(
                  (variety_forecast: any) =>
                    variety_forecast.query_metadata.variety_id == field.variety_id
                )?.variety_name
              }
              `}
              center_header={`Week ${props.week_no}`}
              center_subheader={props.date_string}
              week_no={props.week_no}
              styles={styles}
            ></PageHeader>
            {/* summary cards section */}
            <View style={{ flexDirection: 'row', marginTop: 24 }}>
              {/* latest forecast */}
              <View
                style={{
                  ...styles.info_card,
                  backgroundColor: '#98C9FF',
                  width: '50%'
                }}
              >
                <Text
                  style={{ fontSize: 16, fontWeight: 'medium', marginBottom: 4, textAlign: 'left' }}
                >
                  {props.field_forecasts[
                    field.name + '_' + field.variety_id
                  ]?.forecasts[2]?.toLocaleString()}
                </Text>
                <Text style={{ fontSize: 12, textAlign: 'left' }}>
                  Total Forecast (Current Week)
                </Text>
              </View>

              {/* actuals */}
              <View
                style={{
                  ...styles.info_card,
                  backgroundColor: '#E4707A',
                  width: '50%'
                }}
              >
                <Text
                  style={{ fontSize: 16, fontWeight: 'medium', marginBottom: 4, textAlign: 'left' }}
                >
                  {props?.field_forecasts?.[
                    field.name + '_' + field.variety_id
                  ]?.harvests?.[1].toLocaleString() ?? 'N/A'}
                </Text>
                <Text style={{ fontSize: 12, textAlign: 'left' }}>Actuals (Previous Week)</Text>
              </View>
            </View>

            <View style={{ flexDirection: 'row', gap: 20 }}>
              <View
                style={{
                  marginTop: 10,
                  marginLeft: 10,
                  left: 0,
                  paddingTop: 10,
                  paddingBottom: 20,
                  backgroundColor: '#ffffff',
                  borderRadius: 6,
                  width: '50%'
                }}
              >
                <ReactPDFChart style={{ left: -40 }}>
                  <ComposedChart
                    width={380}
                    height={300}
                    data={props?.field_forecasts?.[field.name + '_' + field.variety_id]?.graph_data}
                    style={{ left: 0 }}
                    barGap={4}
                    margin={{ top: 30 }}
                    // barSize={18}
                  >
                    <XAxis
                      dataKey="name"
                      interval={0}
                      tick={{ fontSize: 11, fontWeight: 'bold' }}
                      axisLine={false}
                    />
                    <YAxis tickCount={6} tick={false} axisLine={false} />
                    <Legend iconType="circle" iconSize={10} width={830} align="left" />
                    <Bar
                      dataKey={'Actuals'}
                      fill={'#626262'}
                      isAnimationActive={false}
                      radius={[3, 3, 0, 0]}
                    >
                      <LabelList
                        dataKey="Actuals"
                        position="top"
                        formatter={(value: number) => (value ? kFormatter(value, 1) : '')}
                        color="#000"
                      />
                    </Bar>
                    <Bar
                      dataKey={'Forecasts'}
                      fill={'#53A5FF'}
                      isAnimationActive={false}
                      radius={[3, 3, 0, 0]}
                    >
                      <LabelList
                        dataKey="Forecasts"
                        position="top"
                        formatter={(value: number) => (value ? kFormatter(value, 1) : '')}
                        color="#000"
                      />
                    </Bar>
                  </ComposedChart>
                </ReactPDFChart>
              </View>

              {/* Grade Splits */}
              <View
                style={{
                  width: '60%',
                  flexDirection: 'column',
                  marginTop: 35,
                  paddingLeft: 35
                }}
              >
                <View style={{ marginTop: 4, alignItems: 'center', width: '100%' }}>
                  <View
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      width: '100%',
                      backgroundColor: '#fff',
                      height: 'auto'
                    }}
                  >
                    {/* body columns */}
                    <View style={{ display: 'flex', flexDirection: 'column' }}>
                      <View
                        style={{
                          padding: '3px 2px 3px 4px',
                          height: '25px',
                          borderBottom: '0.5px solid #F5F5F5',
                          backgroundColor: '#F5F5F5'
                        }}
                      >
                        <Text style={{ fontSize: 8, fontWeight: 'bold' }}></Text>
                      </View>
                      <View
                        style={{
                          // borderTop: week_index == 0 ? '' : '0.5px solid #141414',
                          padding: '3px 2px 3px 4px',
                          height: '25px',
                          borderBottom: '0.5px solid #F5F5F5',
                          backgroundColor: '#80808033'
                        }}
                      >
                        <Text style={{ fontSize: 9, fontWeight: 'medium' }}>Actual</Text>
                      </View>
                      <View
                        style={{
                          padding: '3px 2px 3px 4px',
                          height: '25px',
                          borderBottom: '0.5px solid #F5F5F5',
                          backgroundColor: '#53A5FF33'
                        }}
                      >
                        <Text style={{ fontSize: 9, fontWeight: 'medium' }}>Forecast</Text>
                      </View>

                      {sortVarietyProductionCategories(
                        props.varieties.find(
                          (variety: Variety) => variety.id == field.variety_id
                        ) ?? props.varieties[0]
                      )
                        ?.production_categories.slice()
                        .map((production_category, index) => (
                          <View
                            key={index}
                            style={{
                              padding: '3px 2px 3px 4px',
                              height: '25px',
                              borderBottom: '0.5px solid #F5F5F5',
                              backgroundColor: index % 2 === 0 ? '#fff' : '#FAFAFA'
                            }}
                          >
                            <Text style={{ fontSize: 9 }}>{production_category.name}</Text>
                          </View>
                        ))}
                    </View>
                    {props.graph_data.farm.map((graph_row: any, week_index: number) => (
                      <View
                        key={week_index}
                        style={{ display: 'flex', flexDirection: 'column', width: 'auto', flex: 1 }}
                      >
                        <View
                          style={{
                            padding: '3px 2px 3px 2px',
                            height: '25px',
                            borderBottom: '0.5px solid #F5F5F5',
                            paddingLeft: 8,
                            alignItems: 'center',
                            backgroundColor: '#F5F5F5'
                          }}
                        >
                          <Text style={{ fontSize: 8, fontWeight: 'medium' }}>
                            {graph_row.name}
                          </Text>
                        </View>

                        <View
                          style={{
                            padding: '3px 2px 3px 2px',
                            height: '25px',
                            borderBottom: '0.5px solid #F5F5F5',
                            paddingLeft: 8,
                            alignItems: 'center',
                            backgroundColor: '#80808033'
                          }}
                        >
                          <Text style={{ fontSize: 9, fontWeight: 'medium' }}>
                            {props?.field_forecasts?.[
                              field.name + '_' + field.variety_id
                            ]?.harvests?.[week_index]?.toLocaleString()}
                          </Text>
                        </View>
                        <View
                          style={{
                            // borderTop: week_index == 0 ? '' : '0.5px solid #141414',
                            padding: '3px 2px 3px 2px',
                            height: '25px',
                            borderBottom: '0.5px solid #F5F5F5',
                            paddingLeft: 8,
                            alignItems: 'center',
                            backgroundColor: '#53A5FF33'
                          }}
                        >
                          <Text style={{ fontSize: 9, fontWeight: 'medium' }}>
                            {props.field_forecasts[field.name + '_' + field.variety_id]?.forecasts[
                              week_index
                            ]?.toLocaleString()}
                          </Text>
                        </View>
                        {sortVarietyProductionCategories(
                          props.varieties.find(
                            (variety: Variety) => variety.id == field.variety_id
                          ) ?? props.varieties[0]
                        )
                          ?.production_categories.slice()
                          .map((production_category, index) => (
                            <View
                              key={index}
                              style={{
                                width: 'auto',
                                padding: '3px 2px 3px 2px',
                                height: '25px',
                                borderBottom: '0.5px solid #F5F5F5',
                                alignItems: 'center',
                                backgroundColor: index % 2 === 0 ? '#fff' : '#FAFAFA'
                              }}
                            >
                              <Text style={{ fontSize: 9 }}>
                                {props?.field_forecasts[field.name + '_' + field.variety_id]
                                  ?.detailed_view[week_index]
                                  ? props?.field_forecasts[
                                      field.name + '_' + field.variety_id
                                    ]?.detailed_view[week_index][
                                      production_category.id
                                    ]?.toLocaleString()
                                  : 0}
                              </Text>
                            </View>
                          ))}
                      </View>
                    ))}
                  </View>
                </View>
              </View>
            </View>
          </View>
        </Page>
      ))}
    <Page size="A4" orientation="landscape" style={styles.page}>
      <View
        id="weekly-weather-report"
        style={{
          margin: '10px 30px 30px 10px',
          padding: 10,
          flexGrow: 1
        }}
      >
        <PageHeader
          left_header={`Weather`}
          left_subheader={` ${props.farm.name}, ${props.farm.city.replaceAll('_', ' ')}`}
          center_header={`Week ${props.week_no}`}
          center_subheader={props.date_string}
          week_no={props.week_no}
          styles={styles}
        ></PageHeader>
        {/* Daily Weather section */}
        <View style={{ flexDirection: 'row', display: 'flex', flexWrap: 'nowrap', marginTop: 12 }}>
          {props.weather_data &&
            props.weather_days?.map((weather_day: moment.Moment, index: number) => (
              <View
                key={index}
                style={{
                  width: '100px',
                  marginRight: 16,
                  marginBottom: 8,
                  paddingBottom: 9,
                  borderRadius: 16,
                  backgroundColor: '#b9e5ffff',
                  color: '#000000',
                  fontSize: 12
                }}
              >
                <View
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: 'row',
                    padding: 8,
                    width: '100%',
                    textTransform: 'uppercase'
                  }}
                >
                  <Text style={{ fontSize: 10, fontWeight: 600 }}>{weather_day.format('ddd')}</Text>
                  <Text style={{ fontSize: 10, fontWeight: 600 }}>
                    {weather_day.format('DD/MM')}
                  </Text>
                </View>
                <View
                  style={{
                    flexDirection: 'row',
                    backgroundColor: '#91B0D9',
                    justifyContent: 'center'
                  }}
                >
                  <View style={{ width: '100%', alignItems: 'center' }}>
                    <Image
                      style={{ width: 20, marginTop: 4, marginBottom: 4 }}
                      src={
                        props.weather_data
                          ? props?.weather_data?.weather[index].hourly[0].weatherIconUrl[0].value
                          : ''
                      }
                    ></Image>
                  </View>
                </View>
                <View
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: 'row',
                    padding: '4px 12px 4px 14px',
                    width: '100%'
                  }}
                >
                  <Text style={{ fontSize: 11, fontWeight: 600 }}>
                    H: {props.weather_data.weather[index].maxtempC}°
                  </Text>
                  <Text style={{ fontSize: 11, fontWeight: 600 }}>
                    L: {props.weather_data.weather[index].mintempC}°
                  </Text>
                </View>
                <View style={styles.daily_weather_item}>
                  <Image style={styles.daily_weather_item_icon} src={CloudRain}></Image>
                  <Text style={{ fontSize: 9 }}>
                    {props.weather_data?.weather[index].hourly[0].chanceofrain}%
                  </Text>
                </View>
                <View style={styles.daily_weather_item}>
                  <Image style={styles.daily_weather_item_icon} src={Wind}></Image>
                  <Text style={{ fontSize: 9 }}>
                    {`${props.weather_data?.weather[index].hourly[0].winddir16Point} ${props.weather_data?.weather[index].hourly[0].windspeedKmph} km/h`}
                  </Text>
                </View>
                <View style={styles.daily_weather_item}>
                  <Image style={styles.daily_weather_item_icon} src={Humidity}></Image>
                  <Text style={{ fontSize: 9 }}>
                    {props.weather_data?.weather[index].hourly[0].humidity}%
                  </Text>
                </View>
                <View style={styles.daily_weather_item}>
                  <Image style={{ width: 16, marginRight: 4 }} src={Uv}></Image>
                  <Text style={{ fontSize: 9 }}>
                    {props.weather_data?.weather[index].hourly[0].uvIndex}
                  </Text>
                </View>
              </View>
            ))}
        </View>

        {/* Week Overview Section */}

        {props.weather_week_overview[0] && (
          <View
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
              marginTop: 8,
              width: '100%'
            }}
          >
            {/* Previous Week */}
            <WeeklyWeatherOverview
              week_no={props.week_no}
              styles={styles}
              data={props.weather_week_overview[0]}
            />

            <View
              style={{
                backgroundColor: '#000',
                width: 1,
                height: '95%',
                margin: '0px 16px 0px 10px'
              }}
            ></View>
            {/* Next Week */}
            <WeeklyWeatherOverview
              week_no={props.week_no + 1}
              styles={styles}
              data={props.weather_week_overview[1]}
            />
          </View>
        )}

        <View
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row',
            fontSize: 8,
            marginTop: 2,
            width: '100%'
          }}
        >
          <Text>
            <Text style={{ fontWeight: 'bold' }}>Source:</Text> World Weather Online
          </Text>
          <Text>
            <Text style={{ fontWeight: 'bold' }}>Nearest Weather Station At:</Text>{' '}
            {props?.weather_data?.nearest_area
              ? `${props?.weather_data?.nearest_area[0]?.areaName[0].value} (${props?.weather_data?.nearest_area[0]?.region[0].value}, ${props?.weather_data?.nearest_area[0]?.country[0].value})`
              : ''}
          </Text>
        </View>
      </View>
    </Page>
    <Page size="A4" orientation="landscape" style={styles.page}>
      <View
        style={{
          margin: '10px 30px 30px 10px',
          padding: 10,
          flexGrow: 1
        }}
      >
        <PageHeader
          left_header={`Weather`}
          left_subheader={` ${props.farm.name}, ${props.farm.city.replaceAll('_', ' ')}`}
          center_header={`Week ${props.week_no}`}
          center_subheader={props.date_string}
          week_no={props.week_no}
          styles={styles}
        ></PageHeader>
        <View>
          <Text style={styles.section_title}>Temperature</Text>
          <ReactPDFChart>
            <AreaChart
              height={190}
              width={750}
              data={props.weather_graph_data}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorMin" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8979FF" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#8979FF" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorMax" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FF928A" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#FF928A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" />
              <YAxis>
                <Label value="(°C)" position={'insideLeft'} />
              </YAxis>
              <Legend iconType="circle" iconSize={10} />
              <CartesianGrid strokeDasharray="3 3" />
              <Area
                type="monotone"
                dataKey="Max"
                stroke="#FF928A"
                fillOpacity={0.4}
                fill="#FF928A"
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                dataKey="Min"
                stroke="#8979FF"
                fillOpacity={0.7}
                fill="#8979FF"
                isAnimationActive={false}
              />
            </AreaChart>
          </ReactPDFChart>

          <Text style={styles.section_title}>Rainfall</Text>
          <ReactPDFChart>
            <BarChart width={750} height={190} data={props.weather_graph_data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis>
                <Label value="(mm)" position={'insideLeft'} />
              </YAxis>
              <Legend iconSize={10} />
              <Bar dataKey={'rainfall'} fill={'#8979FF'} isAnimationActive={false}>
                <LabelList
                  dataKey={'rainfall'}
                  position="insideTop"
                  content={(props) => renderLabelList({ ...props, noKFormat: true })}
                  formatter={(value: number) => value && value.toLocaleString()}
                />
              </Bar>
            </BarChart>
          </ReactPDFChart>
          <View
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
              fontSize: 8,
              marginTop: 2,
              width: '100%'
            }}
          >
            <Text>
              <Text style={{ fontWeight: 'bold' }}>Source:</Text> World Weather Online
            </Text>
            <Text>
              <Text style={{ fontWeight: 'bold' }}>Nearest Weather Station At:</Text>{' '}
              {props?.weather_data?.nearest_area
                ? `${props?.weather_data?.nearest_area[0]?.areaName[0].value} (${props?.weather_data?.nearest_area[0]?.region[0].value}, ${props?.weather_data?.nearest_area[0]?.country[0].value})`
                : ''}
            </Text>
          </View>
        </View>
      </View>
    </Page>
  </Document>
);

export default WeeklyPDFReport;
